import axios from "axios";
// import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import Log from "../Services/Log";
import { triggerReload } from "../slices/reduxUtils";
import { BASE_URL } from "../constants/apiEndPoints";
import { showLoader, hideLoader } from "../slices/globalLoader/reducer";
import { ENV_CONFIG } from "../config/environment";

// Set the backend URL using centralized environment configuration
axios.defaults.baseURL = ENV_CONFIG.API_BASE_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";
// axios.defaults.withCredentials = true;

// Logout helper function
const logOut = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userDetails");
  window.location.href = "/login";
};
const PUBLIC_API_PATTERNS = [
  '/public',
  '/global',
  '/open',
  '/pub',
  '/profile/pub', // single profile view for guests
];

const isPublicApi = (url) => {
  if (!url) return false;

  const normalizedUrl = url.toLowerCase();
  return PUBLIC_API_PATTERNS.some((pattern) =>
    normalizedUrl.includes(pattern)
  );
};

let isRefreshing = false;
const refreshAuthToken = async () => {
  if (isRefreshing) {
    return null;
  }
  
  try {
    isRefreshing = true;
    
    // Get the latest refresh token from localStorage
    const refreshToken = localStorage.getItem("refreshToken");
    
    // If no refresh token is available, logout immediately
    if (!refreshToken) {
      console.log("No refresh token available, redirecting to login");
      logOut();
      return null;
    }
    
    // console.log('refreshToken:', refreshToken);
    const response = await axios.post(`${BASE_URL}/refresh/token`, {
      refresh_token: refreshToken,
    });

    const newRefreshToken = response.response.refreshToken;
    const newAccessToken = response.response.accessToken;
    console.log("new AccessToken", newAccessToken);
    localStorage.setItem("accessToken", newAccessToken);
    localStorage.setItem("refreshToken", newRefreshToken);
    
    isRefreshing = false;
    return newAccessToken;
  } catch (error) {
    isRefreshing = false;
    console.log("error", error);
    if (error.response && error.response.status === 401) {
      // Clear the local storage and redirect to login
      console.log("Failed to refresh token, redirecting to login");
      logOut();
    }
    console.error("Failed to refresh token:", error);
    throw error;
  }
};

// Store reference to dispatch function
let storeDispatch = null;

// Function to set dispatch reference
export const setStoreDispatch = (dispatch) => {
  storeDispatch = dispatch;
};

axios.interceptors.request.use(
  function (config) {
   if(!isPublicApi(config.url)){
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
   }
    
    // Show loader for API calls (except refresh token calls and location searches)
    if (storeDispatch && !shouldSkipGlobalLoader(config.url)) {
      const loadingMessage = getLoadingMessage(config.url, config.method);
      storeDispatch(showLoader({ 
        message: loadingMessage, 
        showOverlay: shouldShowOverlay(config.url) 
      }));
    }
    
    return config;
  },
  function (error) {
    console.log("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Helper functions for loader management
const getLoadingMessage = (url, method) => {
  const urlLower = url.toLowerCase();
  
  if (urlLower.includes('login') || urlLower.includes('auth')) {
    return 'Authenticating...';
  } else if (urlLower.includes('business') || urlLower.includes('bureau')) {
    return 'Updating account details...';
  } else if (urlLower.includes('ads')) {
    return 'Loading ads...';
  } else if (urlLower.includes('profile')) {
    return 'Updating profile...';
  } else if (urlLower.includes('dashboard')) {
    return 'Loading dashboard...';
  } else if (method === 'get') {
    return 'Loading data...';
  } else if (method === 'post') {
    return 'Saving data...';
  } else if (method === 'put' || method === 'patch') {
    return 'Updating data...';
  } else if (method === 'delete') {
    return 'Deleting data...';
  }
  
  return 'Loading...';
};

const shouldShowOverlay = (url) => {
  const urlLower = url.toLowerCase();
  
  // Don't show overlay for quick operations
  if (urlLower.includes('notification') || 
      urlLower.includes('status') || 
      urlLower.includes('check')) {
    return false;
  }
  
  return true;
};

const shouldSkipGlobalLoader = (url) => {
  if (!url) return false;
  
  const urlLower = url.toLowerCase();
  
  // Skip global loader for these API calls
  if (urlLower.includes('/refresh/token') ||
      // Skip for master data endpoints (avoid full-screen overlay during dropdown loads)
      urlLower.includes('/masterdata') ||
      urlLower.includes('/location/places') ||
      urlLower.includes('/location') ||
      urlLower.includes('/places') ||
      urlLower.includes('/search/place') ||
      urlLower.includes('/geocode') ||
      urlLower.includes('/autocomplete')) {
    return true;
  }
  
  return false;
};

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    // Hide loader on successful response (only if we showed it in the first place)
    if (storeDispatch && !shouldSkipGlobalLoader(response.config?.url)) {
      storeDispatch(hideLoader());
    }
    return response.data ? response.data : response;
  },
  async function (error) {
    // Hide loader on error response (only if we showed it in the first place)
    if (storeDispatch && !shouldSkipGlobalLoader(error.config?.url)) {
      storeDispatch(hideLoader());
    }
    
    let message;
    const originalRequest = error.config;

    if (error.response) {
      switch (error.response.status) {
        case 500:
          message = "Internal Server Error. Please try again later.";
          break;
        case 401:
          const isPublicRequest = isPublicApi(originalRequest?.url);

          if (isPublicRequest) {
            message =
              error?.response?.data?.message ||
              "Authentication required to access this resource.";
            return Promise.reject(message);
          }

          if (!originalRequest._retry) {
            console.log("401 Unauthorized for request:", originalRequest.url);
            
            // Check if this is already a refresh token request
            if (originalRequest.url === `${BASE_URL}/refresh/token`) {
              console.log("Refresh token request failed, redirecting to login");
              logOut();
              break;
            }

            // For auth endpoints (e.g., login, verify otp), do NOT redirect to login.
            // Let the caller handle the error message from backend.
            const urlLowerFor401 = (originalRequest.url || '').toLowerCase();
            const isAuthEndpoint = urlLowerFor401.includes('/auth/login') ||
                                   urlLowerFor401.includes('/auth/verify/otp') ||
                                   urlLowerFor401.includes('/auth/change-phone') ||
                                   urlLowerFor401.includes('/auth/verify-phone-change');
            if (isAuthEndpoint) {
              message = error?.response?.data?.message || "Invalid credentials";
              return Promise.reject(message);
            }

            // Check if we have an access token to begin with
            const accessToken = localStorage.getItem("accessToken");
            const refreshToken = localStorage.getItem("refreshToken");

            if (!accessToken && !refreshToken && !isPublicRequest) {
              console.log("No tokens available, redirecting to login");
              logOut();
              break;
            }

            originalRequest._retry = true;
            try {
              const newToken = await refreshAuthToken();
              if (newToken) {
                console.log("Token refreshed successfully, triggering reload");
                triggerReload();
              } else {
                console.log("Failed to get new token, redirecting to login");
                logOut();
              }
            } catch (refreshError) {
              console.log("Error refreshing token:", refreshError);
              logOut();
            }
          } else {
            message = "Invalid credentials";
          }
          break;
        case 404:
          message = "Sorry! The data you are looking for could not be found.";
          break;
        case 400:
          message =
            error?.response?.data?.message ||
            error.message ||
            "An unexpected error occurred.";
          break;
        case 413:
          message =
            "The file size is too large to process. Please try uploading a smaller file.";
          break;
        case 502:
          message =
            "Bad Gateway. The server is currently unavailable. Please try again later.";
          break;
        default:
          message = error.message || "An unexpected error occurred.";
        // message = data && data?.message || error.message || "An unexpected error occurred.";
      }
    } else if (error.request) {
      if (!error)
        if (!originalRequest._retry) {
          // Retry logic for network issues or server not responding
          originalRequest._retry = true;
          try {
            return axios(originalRequest);
          } catch (retryError) {
            message = "Request failed. Retrying...";
          }
          triggerReload();
        } else {
          message = "An unexpected error occurred";
        }
    } else {
      message = error.message || "An unexpected error occurred";
    }

    // toast.error(message, {
    //   position: toast.POSITION.TOP_RIGHT,
    //   autoClose: 5000,
    // });

    return Promise.reject(message);
  }
);

const setAuthorization = (token) => {
  axios.defaults.headers.common["Authorization"] = token;
};

class APIClient {
  get = (url, params) => {
    let response;

    if (params) {
      let paramKeys = [];

      Object.keys(params).map((key) => {
        paramKeys.push(key + "=" + params[key]);
        return paramKeys;
      });

      const queryString =
        paramKeys && paramKeys.length ? paramKeys.join("&") : "";
      response = axios.get(`${url}?${queryString}`);
    } else {
      response = axios.get(url);
    }

    return response;
  };

  create = (url, data) => {
    return axios.post(url, data);
  };

  update = (url, data) => {
    return axios.patch(url, data);
  };

  put = (url, data) => {
    return axios.put(url, data);
  };

  delete = (url, config) => {
    return axios.delete(url, { ...config });
  };
}

const getLoggedinUser = () => {
  const user = localStorage.getItem("userDetails");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

const api = new APIClient();

export { APIClient, setAuthorization, getLoggedinUser, api };
